/**
 * HotelConnectorsReadOnly — Read-only connector view for the hotel portal.
 * Displays connectors linked to the current hotel without modification actions.
 * Requirement: 21.3
 */
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { getStoredHotelId } from '../../services/authService';

interface ConnectorHealth {
  status: string;
  last_sync_at?: string;
  error_count: number;
  latency_ms?: number;
}

interface ConnectorCatalog {
  connector_type: string;
  protocol: string;
}

interface ConnectorInstance {
  id: string;
  name: string;
  status: string;
  entity_type: string;
  catalog?: ConnectorCatalog;
  health?: ConnectorHealth;
  created_at: string;
}

const healthColor: Record<string, string> = {
  ONLINE: 'bg-green-100 text-green-800',
  OFFLINE: 'bg-gray-100 text-gray-600',
  DEGRADED: 'bg-yellow-100 text-yellow-800',
  ERROR: 'bg-red-100 text-red-800',
};

const statusColor: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
  DRAFT: 'bg-blue-100 text-blue-700',
  ERROR: 'bg-red-100 text-red-800',
};

export default function HotelConnectorsReadOnly() {
  const [instances, setInstances] = useState<ConnectorInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const fetchConnectors = async () => {
      setLoading(true);
      setError('');
      try {
        // Prefer explicit entity_id — principal JWTs often omit hotel_id claim.
        const hotelId = getStoredHotelId();
        const res = await api.get('/api/v1/connectors/instances', {
          params: {
            entity_type: 'hotel',
            ...(hotelId ? { entity_id: hotelId } : {}),
          },
          signal: controller.signal,
          timeout: 10_000,
        });
        const data = res.data?.data ?? res.data?.instances ?? res.data;
        setInstances(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('[HotelConnectorsReadOnly] Erreur:', err);
          const detail =
            err?.response?.data?.error?.message ||
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message;
          setError(
            detail
              ? `Impossible de charger les connecteurs (${typeof detail === 'string' ? detail : JSON.stringify(detail)})`
              : 'Impossible de charger les connecteurs'
          );
          setInstances([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchConnectors();
    return () => controller.abort();
  }, []);

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Intégrations API</h2>
        <p className="text-sm text-gray-500 mt-1">
          Connecteurs PMS / REST / DIEF Soft liés à votre établissement (SCRUM-11). Lecture seule — la configuration est gérée par la plateforme.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Skeleton loader */}
      {loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
          <div className="h-10 bg-gray-100" />
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-4 p-4 border-b border-gray-100">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-4 bg-gray-200 rounded w-1/6" />
              <div className="h-4 bg-gray-200 rounded w-1/6" />
              <div className="h-4 bg-gray-200 rounded w-1/6" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && instances.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 text-gray-500 px-4 py-12 rounded text-center">
          <p className="font-medium mb-1">Aucune intégration API active</p>
          <p className="text-sm">
            Demandez à un administrateur plateforme (superadmin) d&apos;activer un connecteur SDK DIEF ou PMS REST pour cet hôtel.
          </p>
        </div>
      )}

      {/* Read-only table */}
      {!loading && instances.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 text-xs">
                <th className="p-3">Nom</th>
                <th className="p-3">Type</th>
                <th className="p-3">Protocole</th>
                <th className="p-3">Statut</th>
                <th className="p-3">Santé</th>
                <th className="p-3">Dernier sync</th>
                <th className="p-3">Latence</th>
              </tr>
            </thead>
            <tbody>
              {instances.map(inst => {
                const hs = inst.health?.status || 'OFFLINE';
                return (
                  <tr key={inst.id} className="border-b">
                    <td className="p-3 font-medium">{inst.name}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">
                        {inst.catalog?.connector_type || '—'}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-600">{inst.catalog?.protocol || '—'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${statusColor[inst.status] || 'bg-gray-100 text-gray-600'}`}>
                        {inst.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${healthColor[hs] || 'bg-gray-100 text-gray-600'}`}>
                        {hs}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">{formatDate(inst.health?.last_sync_at)}</td>
                    <td className="p-3 text-xs text-gray-500">
                      {inst.health?.latency_ms != null ? `${inst.health.latency_ms}ms` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
